import React, { useEffect, useRef, useState } from 'react';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import styled from 'styled-components';
import { navDelay, loaderDelay } from '@utils';
import { usePrefersReducedMotion } from '@hooks';
import { resumeUrl } from '@config';

const StyledHeroSection = styled.section`
  ${({ theme }) => theme.mixins.flexCenter};
  flex-direction: column;
  align-items: flex-start;
  min-height: 100vh;
  height: 100vh;
  padding: 0;

  @keyframes wave {
    0% {
      transform: rotate(0deg);
    }
    10% {
      transform: rotate(14deg);
    }
    20% {
      transform: rotate(-8deg);
    }
    30% {
      transform: rotate(14deg);
    }
    40% {
      transform: rotate(-4deg);
    }
    50% {
      transform: rotate(10deg);
    }
    60% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(0deg);
    }
  }

  .wave-emoji {
    display: inline-block;
    padding: 0;
    border: 0;
    background: transparent;
    line-height: 1;
    transform-origin: 70% 70%;
    cursor: pointer;
    user-select: none;
  }

  .wave-emoji.is-waving {
    animation: wave 2s ease-in-out 1;
  }

  @media (max-height: 700px) and (min-width: 700px), (max-width: 360px) {
    height: auto;
    padding-top: var(--nav-height);
  }

  h1 {
    margin: 0 0 30px 4px;
    color: var(--green);
    font-family: var(--font-mono);
    font-size: clamp(var(--fz-sm), 5vw, var(--fz-md));
    font-weight: 400;

    @media (max-width: 480px) {
      margin: 0 0 20px 2px;
    }
  }

  h3 {
    margin-top: 5px;
    color: var(--slate);
    line-height: 0.9;
  }

  p {
    margin: 20px 0 0;
    max-width: 540px;
  }

  .cta-row {
    margin-top: 50px;
    display: flex;
    gap: 14px;
    flex-wrap: wrap;
    align-items: center;
  }

  .cta-primary,
  .cta-secondary {
    ${({ theme }) => theme.mixins.bigButton};
  }

  .cta-secondary {
    background-color: transparent;
    color: var(--green);

    &:hover,
    &:focus {
      background-color: var(--lightest-navy);
    }
  }
`;

const Hero = () => {
  const [isMounted, setIsMounted] = useState(false);
  const prefersReducedMotion = usePrefersReducedMotion();
  const [isWaving, setIsWaving] = useState(false);
  const waveTimeoutRef = useRef(null);

  const triggerWave = () => {
    if (prefersReducedMotion) {
      return;
    }

    setIsWaving(false);
    requestAnimationFrame(() => setIsWaving(true));

    if (waveTimeoutRef.current) {
      clearTimeout(waveTimeoutRef.current);
    }
    waveTimeoutRef.current = setTimeout(() => setIsWaving(false), 2000);
  };

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    const timeout = setTimeout(() => setIsMounted(true), navDelay);
    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    if (!isMounted) {
      return;
    }
    triggerWave();
    return () => {
      if (waveTimeoutRef.current) {
        clearTimeout(waveTimeoutRef.current);
      }
    };
  }, [isMounted]);

  const one = (
    <h1>
      Hi{' '}
      <button
        type="button"
        className={`wave-emoji${isWaving ? ' is-waving' : ''}`}
        onPointerEnter={triggerWave}
        onFocus={triggerWave}
        onClick={triggerWave}
        aria-label="Wave">
        <span role="img" aria-hidden="true">
          👋
        </span>
      </button>
      , my name is
    </h1>
  );
  const two = <h2 className="big-heading">Oyero Habibulah.</h2>;
  const three = <h3 className="big-heading">I build fast, scalable products people rely on.</h3>;
  const four = (
    <>
      <p>
        I&apos;m a Full Stack Product Engineer helping startups and growing teams build scalable,
        business-driven systems users actually enjoy using.
      </p>
    </>
  );
  const five = (
    <div className="cta-row">
      <a className="cta-primary" href="/#projects">
        See What I&apos;ve Built
      </a>
      <a className="cta-secondary" href={resumeUrl} target="_blank" rel="noreferrer">
        Book a Free Strategy Call
      </a>
    </div>
  );

  const items = [one, two, three, four, five];

  return (
    <StyledHeroSection>
      {prefersReducedMotion ? (
        <>
          {items.map((item, i) => (
            <div key={i}>{item}</div>
          ))}
        </>
      ) : (
        <TransitionGroup component={null}>
          {isMounted &&
            items.map((item, i) => (
              <CSSTransition key={i} classNames="fadeup" timeout={loaderDelay}>
                <div style={{ transitionDelay: `${i + 1}00ms` }}>{item}</div>
              </CSSTransition>
            ))}
        </TransitionGroup>
      )}
    </StyledHeroSection>
  );
};

export default Hero;
